/**
 * Dealer Plates tab body. Two stacked sections:
 *
 *   1. Dealer logo panel — current logo thumbnail (or empty-state)
 *      plus an Upload Logo button. The logo is what the generator
 *      draws from; a dealer with no logo can still upload plates,
 *      they just can't generate one.
 *   2. Plates list — beefy rows mirroring the Reference Library row
 *      pattern (96×72 thumbnail, inline-editable name, default radio,
 *      delete trash icon). Default first, newest next.
 *
 * Phase 4 worker pickup uses the default plate; per-rooftop and per-
 * vehicle plate routing is deferred (plan section 9).
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Trash2, Upload } from "lucide-react"

import { THEME } from "../../config/theme"
import {
  DealerLogo,
  DealerPlate,
  RooftopPlateAssignment,
  getDealerLogo,
  listDealerPlates,
  listRooftopPlateAssignments,
  setRooftopPlate,
  updateDealerPlate,
  uploadDealerLogo,
} from "../../api/dealerPlates"
import AddPlateModal from "./AddPlateModal"
import DeletePlateConfirm from "./DeletePlateConfirm"

interface Props {
  dealerGroupId: number
  // The page-level dealer selector shows the dealer's name; the view
  // body doesn't need to repeat it. Kept here as a future-friendly
  // handle for empty-state copy and aria labels.
  dealerGroupName: string
}

const ACCEPTED_LOGO_TYPES = "image/png,image/jpeg"


export default function DealerPlatesView({ dealerGroupId, dealerGroupName }: Props) {
  const [logo, setLogo] = useState<DealerLogo | null>(null)
  const [plates, setPlates] = useState<DealerPlate[]>([])
  const [assignments, setAssignments] = useState<RooftopPlateAssignment[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState<boolean>(false)
  const [confirmDelete, setConfirmDelete] = useState<DealerPlate | null>(null)
  const [logoUploading, setLogoUploading] = useState<boolean>(false)
  const logoInputRef = useRef<HTMLInputElement | null>(null)

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [logoResult, platesResult, assignmentsResult] = await Promise.all([
        getDealerLogo(dealerGroupId),
        listDealerPlates(dealerGroupId),
        listRooftopPlateAssignments(dealerGroupId),
      ])
      setLogo(logoResult)
      setPlates(platesResult.plates)
      setAssignments(assignmentsResult.assignments)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load plates")
    } finally {
      setLoading(false)
    }
  }, [dealerGroupId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const [logoResult, platesResult, assignmentsResult] = await Promise.all([
          getDealerLogo(dealerGroupId),
          listDealerPlates(dealerGroupId),
          listRooftopPlateAssignments(dealerGroupId),
        ])
        if (!cancelled) {
          setLogo(logoResult)
          setPlates(platesResult.plates)
          setAssignments(assignmentsResult.assignments)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load plates")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [dealerGroupId])

  const handleLogoFileChange = async (file: File | null) => {
    if (!file) return
    setLogoUploading(true)
    setError(null)
    try {
      const updated = await uploadDealerLogo(dealerGroupId, file)
      setLogo(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logo upload failed")
    } finally {
      setLogoUploading(false)
      if (logoInputRef.current) logoInputRef.current.value = ""
    }
  }

  const handleSetDefault = async (plate: DealerPlate) => {
    // Optimistically flip the radio so the click feels instant; the
    // backend call follows. On failure we reload to recover.
    setPlates((current) => current.map((p) => ({
      ...p, is_default: p.id === plate.id,
    })))
    try {
      await updateDealerPlate(dealerGroupId, plate.id, { is_default: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set default")
      await loadAll()
    }
  }

  const handleRename = async (plate: DealerPlate, nextName: string) => {
    const trimmed = nextName.trim()
    if (!trimmed || trimmed === plate.name) return
    try {
      const updated = await updateDealerPlate(dealerGroupId, plate.id, { name: trimmed })
      setPlates((current) => current.map((p) => (p.id === updated.id ? updated : p)))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rename failed")
      // Revert by reloading so the input reflects what's really on the server.
      await loadAll()
    }
  }

  const handlePlateAdded = (plate: DealerPlate) => {
    setAdding(false)
    setPlates((current) => {
      // The new plate may have been auto-defaulted server-side (first
      // plate for the dealer). If so, demote any prior defaults
      // locally to keep the radio invariant correct without a refetch.
      const next = current.map((p) => (
        plate.is_default ? { ...p, is_default: false } : p
      ))
      return [plate, ...next.filter((p) => p.id !== plate.id)]
    })
  }

  const handlePlateDeleted = () => {
    const deletedId = confirmDelete?.id
    setConfirmDelete(null)
    if (deletedId !== undefined) {
      setPlates((current) => current.filter((p) => p.id !== deletedId))
      // The FK is ON DELETE SET NULL — every rooftop that was pointing
      // at this plate now inherits the group default. Mirror that
      // locally so the dropdown reflects the new state without a refetch.
      setAssignments((current) => current.map((a) => (
        a.default_plate_id === deletedId
          ? { ...a, default_plate_id: null, default_plate_name: null }
          : a
      )))
    }
  }

  const handleAssignmentChange = async (
    rooftopId: number, plateId: number | null,
  ) => {
    // Optimistically swap the row so the dropdown change feels instant.
    const plate = plateId !== null ? plates.find((p) => p.id === plateId) : null
    setAssignments((current) => current.map((a) => (
      a.rooftop_id === rooftopId
        ? {
          ...a,
          default_plate_id: plateId,
          default_plate_name: plate ? plate.name : null,
        }
        : a
    )))
    try {
      const updated = await setRooftopPlate(rooftopId, plateId)
      setAssignments((current) => current.map((a) => (
        a.rooftop_id === updated.rooftop_id ? updated : a
      )))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update rooftop plate")
      await loadAll()
    }
  }

  // Derived count: how many rooftops point at each plate via override.
  // Drives the "Used by N rooftops" line under each plate. Computed
  // from `assignments` so optimistic updates land instantly without a
  // refetch of the plates list.
  const rooftopCountByPlateId = new Map<number, number>()
  for (const a of assignments) {
    if (a.default_plate_id !== null) {
      rooftopCountByPlateId.set(
        a.default_plate_id,
        (rooftopCountByPlateId.get(a.default_plate_id) ?? 0) + 1,
      )
    }
  }

  const groupDefaultPlate = plates.find((p) => p.is_default) ?? null

  if (loading) {
    return <div style={loadingTextStyle}>Loading…</div>
  }

  return (
    <div>
      {error && <div style={errorBannerStyle}>{error}</div>}

      {/* Logo panel */}
      <section style={panelStyle}>
        <div style={panelHeaderStyle}>
          <span style={panelTitleStyle}>Dealer Logos</span>
          <input
            ref={logoInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES}
            style={{ display: "none" }}
            onChange={(e) => handleLogoFileChange(e.target.files?.[0] ?? null)}
          />
          <button
            onClick={() => logoInputRef.current?.click()}
            disabled={logoUploading}
            style={secondaryButtonStyle}
          >
            <Upload size={14} style={{ marginRight: 6 }} />
            {logoUploading
              ? "Uploading…"
              : logo ? "Replace Logo" : "Upload Logo"}
          </button>
        </div>
        {logo ? (
          <img
            src={logo.logo_url}
            alt={`${dealerGroupName} logo`}
            style={logoImageStyle}
          />
        ) : (
          <div style={logoEmptyStyle}>
            No logo uploaded yet for <strong>{dealerGroupName}</strong>. Upload
            one to enable the Generate-from-Logo flow when adding a plate.
          </div>
        )}
      </section>

      {/* Plates panel */}
      <section style={panelStyle}>
        <div style={panelHeaderStyle}>
          <span style={panelTitleStyle}>
            Plates {plates.length > 0 && `(${plates.length})`}
          </span>
          <button
            onClick={() => setAdding(true)}
            style={secondaryButtonStyle}
          >
            + Add Plate
          </button>
        </div>

        {plates.length === 0 ? (
          <div style={emptyListStyle}>
            No plates yet — add one to get started.
          </div>
        ) : (
          <div style={listContainerStyle}>
            <div style={listHeaderStyle}>
              <span style={listColumnLabelStyle}>Thumbnail</span>
              <span style={listColumnLabelStyle}>Plate Name</span>
              <span style={{
                ...listColumnLabelStyle, textAlign: "center",
              }}>Default</span>
              <span style={listColumnLabelStyle}></span>
            </div>
            {plates.map((plate) => (
              <PlateRow
                key={plate.id}
                plate={plate}
                rooftopCount={rooftopCountByPlateId.get(plate.id) ?? 0}
                onSetDefault={() => handleSetDefault(plate)}
                onRename={(next) => handleRename(plate, next)}
                onDelete={() => setConfirmDelete(plate)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Rooftop assignments panel */}
      <section style={panelStyle}>
        <div style={panelHeaderStyle}>
          <span style={panelTitleStyle}>Rooftop Assignments</span>
        </div>
        <p style={panelSubtitleStyle}>
          Which plate goes on each rooftop's photos? Leave a rooftop on
          "Group default" to inherit the plate marked default above.
        </p>
        {assignments.length === 0 ? (
          <div style={emptyListStyle}>
            No active rooftops in this group yet.
          </div>
        ) : (
          <div style={assignmentListStyle}>
            {assignments.map((assignment) => (
              <RooftopAssignmentRow
                key={assignment.rooftop_id}
                assignment={assignment}
                plates={plates}
                groupDefaultPlate={groupDefaultPlate}
                onChange={(plateId) => handleAssignmentChange(
                  assignment.rooftop_id, plateId,
                )}
              />
            ))}
          </div>
        )}
      </section>

      {adding && (
        <AddPlateModal
          dealerGroupId={dealerGroupId}
          dealerLogo={logo}
          onClose={() => setAdding(false)}
          onSaved={handlePlateAdded}
        />
      )}

      {confirmDelete && (
        <DeletePlateConfirm
          dealerGroupId={dealerGroupId}
          plate={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onDeleted={handlePlateDeleted}
        />
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Plate row — autosave-on-blur name input + default radio + delete icon
// ---------------------------------------------------------------------------

interface PlateRowProps {
  plate: DealerPlate
  rooftopCount: number
  onSetDefault: () => void
  onRename: (nextName: string) => void
  onDelete: () => void
}

function PlateRow({
  plate, rooftopCount, onSetDefault, onRename, onDelete,
}: PlateRowProps) {
  const [name, setName] = useState<string>(plate.name)

  // Keep the local input in sync if the parent reloads (e.g. after a
  // rename collision triggered a refetch).
  useEffect(() => {
    setName(plate.name)
  }, [plate.name])

  return (
    <div style={rowStyle}>
      <div style={thumbnailCellStyle}>
        <img
          src={plate.thumbnail_url}
          alt={`${plate.name} plate`}
          style={thumbnailImageStyle}
        />
      </div>
      <div style={nameCellStyle}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => onRename(name)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          style={nameInputStyle}
        />
        <span style={sourceTagStyle}>
          {plate.source === "generated" ? "Generated" : "Uploaded"}
        </span>
        {rooftopCount > 0 && (
          <span style={usageTagStyle}>
            Used by {rooftopCount} rooftop{rooftopCount === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div style={defaultCellStyle}>
        <input
          type="radio"
          name="default-plate"
          checked={plate.is_default}
          onChange={onSetDefault}
          aria-label={`Set ${plate.name} as default`}
          style={radioStyle}
        />
      </div>
      <div style={deleteCellStyle}>
        <button
          onClick={onDelete}
          aria-label={`Delete ${plate.name}`}
          style={deleteIconButtonStyle}
        >
          <Trash2 size={16} color={THEME.colors.semantic.danger} />
        </button>
      </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Rooftop assignment row — name + dropdown of (Group default | each plate)
// ---------------------------------------------------------------------------

interface RooftopAssignmentRowProps {
  assignment: RooftopPlateAssignment
  plates: DealerPlate[]
  groupDefaultPlate: DealerPlate | null
  onChange: (plateId: number | null) => void
}

// Sentinel value for the "Group default" option in the <select>. The
// element's value is always a string; an empty string distinguishes
// "inherit" from any real plate id (always > 0).
const INHERIT_VALUE = ""

function RooftopAssignmentRow({
  assignment, plates, groupDefaultPlate, onChange,
}: RooftopAssignmentRowProps) {
  const currentValue = assignment.default_plate_id === null
    ? INHERIT_VALUE
    : String(assignment.default_plate_id)

  const groupDefaultLabel = groupDefaultPlate !== null
    ? `Group default (${groupDefaultPlate.name})`
    : "Group default (no plate set)"

  return (
    <div style={assignmentRowStyle}>
      <span style={rooftopNameStyle}>{assignment.rooftop_name}</span>
      <select
        value={currentValue}
        onChange={(e) => {
          const next = e.target.value
          onChange(next === INHERIT_VALUE ? null : Number(next))
        }}
        style={dropdownStyle}
        aria-label={`Plate for ${assignment.rooftop_name}`}
      >
        <option value={INHERIT_VALUE}>{groupDefaultLabel}</option>
        {plates.map((plate) => (
          <option key={plate.id} value={String(plate.id)}>
            {plate.name}
          </option>
        ))}
      </select>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const errorBannerStyle: React.CSSProperties = {
  background: THEME.colors.danger,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

const loadingTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
}

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

const panelHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.md,
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
}

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const logoImageStyle: React.CSSProperties = {
  maxWidth: 280,
  maxHeight: 140,
  objectFit: "contain",
  background: THEME.colors.lightGray,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.sm,
}

const logoEmptyStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  padding: THEME.spacing.md,
  background: THEME.colors.lightGray,
  border: `1px dashed ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  lineHeight: 1.4,
}

const emptyListStyle: React.CSSProperties = {
  fontSize: 14,
  color: THEME.colors.neutral.light.textSecondary,
  padding: THEME.spacing.lg,
  background: THEME.colors.lightGray,
  border: `1px dashed ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  textAlign: "center",
}

// Plates list grid: thumbnail, name + source tag, default radio, delete.
// Same 96×72 thumbnail size as Reference Library rows so the rhythm
// across the page stays consistent.
const ROW_GRID = "120px 1fr 100px 60px"

const listContainerStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  overflow: "hidden",
}

const listHeaderStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: ROW_GRID,
  gap: THEME.spacing.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.lightGray,
  alignItems: "center",
}

const listColumnLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: ROW_GRID,
  gap: THEME.spacing.md,
  alignItems: "center",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
}

const thumbnailCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
}

const thumbnailImageStyle: React.CSSProperties = {
  width: 96,
  height: 72,
  objectFit: "contain",
  background: THEME.colors.lightGray,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: 4,
}

const nameCellStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
}

const nameInputStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid transparent`,
  borderRadius: THEME.borderRadius.sm,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

const sourceTagStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}

const defaultCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const radioStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  cursor: "pointer",
  accentColor: THEME.colors.navy,
}

const deleteCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const deleteIconButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  borderRadius: THEME.borderRadius.sm,
}

const usageTagStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  fontStyle: "italic",
}

const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

const assignmentListStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  overflow: "hidden",
}

const assignmentRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 280px",
  gap: THEME.spacing.md,
  alignItems: "center",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
}

const rooftopNameStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
}

const dropdownStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 13,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  cursor: "pointer",
  width: "100%",
}
